<template>
  <div id="prizes">
    <h5 class="title">{{ $t('promotion.luckyDraw.topPrizes') }}</h5>
    <div class="top-container">
      <div v-for="(item, i) in topPrizes" :key="i" class="outer-container">
        <div class="inner-container">
          <p class="header">{{ $t(`promotion.luckyDraw.prizes.${item.rank + 1}`) }}</p>
          <div class="divider"></div>
          <img :src="item.image" alt="prizeImage" />
          <p class="description">{{ item.quantity }} x {{ item.name }}</p>
        </div>
        <div class="circle-container circle-container-2">
          <div class="circle"></div>
        </div>
      </div>
    </div>
    <div v-if="otherPrizes">
      <h5 class="mt-5 mb-4 sub-title">{{ $t('promotion.luckyDraw.otherPrizes') }}</h5>
      <div class="bottom-container">
        <div class="scrollable-items-container">
          <div
            v-for="(item, i) in otherPrizes"
            :key="i"
            :class="{ 'x-scrollable-item': true, 'y-scrollable-item': scrollableItemType === 'y' }"
          >
            <img :src="item.image" alt="prizeImage" />
            <p class="description">{{ item.quantity }} x {{ item.name }}</p>
            <div class="overlay-index">{{ $t(`promotion.luckyDraw.prizes.${item.rank + 1}`) }}</div>
          </div>
        </div>
        <div v-if="showScrollLeftButton" class="overlay-scroll left" @click="scroll('right')">
          <i class="el-icon-arrow-left"></i>
        </div>
        <div v-if="showScrollRightButton" class="overlay-scroll right" @click="scroll('left')">
          <i class="el-icon-arrow-right"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { filter } from '@chenfengyuan/vue-qrcode';

export default {
  props: {
    prizeList: {
      type: Array,
      default: () => []
    },
    langUsed: {
      type: String,
      default: () => 'en_US'
    }
  },
  data() {
    return {
      showScrollLeftButton: false,
      showScrollRightButton: false,
      scrollableItemType: undefined,
      topPrizes: undefined,
      otherPrizes: undefined
    };
  },
  watch: {
    prizeList() {
      this.scrollableItemType = undefined;
      this.topPrizes = undefined;
      this.otherPrizes = undefined;
      this.loadPrizes();
    },
    otherPrizes(value) {
      if (value) {
        this.$nextTick(() => {
          this.checkScrollableItemType();
          this.checkOverflow();
          document.querySelector('.scrollable-items-container').addEventListener('scroll', this.checkOverflow);
          window.addEventListener('resize', this.checkScrollableItemType);
        });
      }
    }
  },
  methods: {
    mapPrize(prizeList) {
      return prizeList.map(prize => {
        return {
          rank: prize.rank,
          name: prize.selectedPrizeName,
          quantity: prize.quantity,
          image: prize.filePath ? `${window.location.origin}/api${prize.filePath}` : ''
        };
      });
    },
    async loadPrizes() {
      let filteredPrize = this.mapPrize(this.prizeList.filter(prize => [0, 1, 2].includes(prize.rank)));
      const secondPrize = filteredPrize.splice(1, 1)[0];
      filteredPrize.unshift(secondPrize);
      this.topPrizes = filteredPrize;

      if (this.prizeList.length > 3) {
        this.otherPrizes = this.mapPrize(this.prizeList.filter(prize => ![0, 1, 2].includes(prize.rank)));
      }
    },
    scroll(direction) {
      const container = document.querySelector('.scrollable-items-container');
      const element = document.querySelector('.x-scrollable-item');
      if (direction === 'left') container.scrollLeft += element.clientWidth + 10;
      if (direction === 'right') container.scrollLeft -= element.clientWidth + 10;
    },
    checkOverflow() {
      const container = document.querySelector('.scrollable-items-container');
      const isShow = container.scrollWidth > container.clientWidth;
      if (this.langUsed === 'ar') {
        this.showScrollLeftButton = isShow && container.scrollLeft > container.clientWidth - container.scrollWidth + 1;
        this.showScrollRightButton = isShow && container.scrollLeft < 0;
      } else {
        this.showScrollLeftButton = isShow && container.scrollLeft > 0;
        this.showScrollRightButton =
          isShow && container.scrollLeft + container.clientWidth + 1 <= container.scrollWidth;
      }
    },
    checkScrollableItemType() {
      const container = document.querySelector('.scrollable-items-container');
      const elementWidth = 196; // 180 container + 16 gap
      this.scrollableItemType = this.otherPrizes.length * elementWidth > container.clientWidth ? 'y' : 'x';
    }
  },
  mounted() {
    this.loadPrizes();
  },
  beforeDestroy() {
    document.querySelector('.scrollable-items-container').removeEventListener('scroll', this.checkOverflow);
    window.removeEventListener('resize', this.checkScrollableItemType);
  }
};
</script>

<style lang="scss" scoped>
#prizes {
  .title {
    margin-bottom: 1.5rem;
    font-weight: 700;
    font-size: 24px;
    color: #00091f;
  }
  .sub-title {
    margin-bottom: 1.5rem;
    font-weight: 800;
    font-size: 20px;
    color: #1d1d1f;
  }
  .top-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 24px;

    .outer-container {
      flex: 1 0 calc(33.333% - 24px);
      display: flex;
      position: relative;
      background-color: #fffcfc;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      min-width: 282px;
      min-height: 282px;
      border: 2px solid #f5f5f7;

      @media (max-width: 1295px) {
        &:nth-child(odd) {
          flex: 1 0 calc(50%-24px);
        }
        &:nth-child(2) {
          order: -1;
          flex: 2 0 100%;
        }
      }
    }

    .inner-container {
      position: absolute;
      width: calc(100% - 16px);
      height: calc(100% - 16px);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 1;
      .header {
        color: #000;
        font-weight: bold;
        margin-bottom: 8px;
        font-size: 16px;
      }
      .divider {
        height: 3px;
        width: 16px;
        background-color: $primary-blue;
        border-radius: 10px;
        margin-bottom: 24px;
      }
      img {
        width: 90px;
        margin-bottom: 24px;
      }
      .description {
        width: 80%;
        font-weight: 400;
        color: #000;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: center;
        @supports (-webkit-line-clamp: 3) {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: initial;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }
      }
    }

    .circle-container {
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
      border-radius: 6px;
    }
    .circle {
      position: absolute;
      clip-path: polygon(68% 37%, 100% 83%, 100% 100%, 68% 100%, 38% 57%);
      width: 375px;
      height: 285px;
    }
    .circle-container-1 {
      .circle:nth-child(1) {
        top: -5%;
        left: -5%;
        background: #034854;
      }
      .circle:nth-child(2) {
        top: 75%;
        left: 40%;
        transform: translate(-60%, -50%);
        background: linear-gradient(143deg, #034854 18.24%, rgba(3, 72, 84, 0.2) 82.03%);
        width: 60px;
        height: 60px;
      }
      .circle:nth-child(3) {
        top: 55%;
        left: 30%;
        background: linear-gradient(207deg, #034854 21.01%, rgba(210, 97, 55, 0) 76.79%);
        width: 170px;
        height: 170px;
      }
    }
    .circle-container-2 {
      .circle {
        top: 0;
        right: 0;
        background: rgb(255, 255, 255);
        background: linear-gradient(45deg, rgba(255, 255, 255, 1) 0%, rgba(224, 236, 255, 1) 100%);
        opacity: 0.5;
      }
    }
  }

  .bottom-container {
    position: relative;
    overflow: hidden;
    border-radius: 8px;

    .scrollable-items-container {
      position: relative;
      display: flex;
      overflow-x: auto;
      column-gap: 10px;
      scroll-behavior: smooth;
      scrollbar-width: none; /* Hide scrollbar on Firefox */
      -ms-overflow-style: none; /* Hide scrollbar on IE & Edge */

      &::-webkit-scrollbar {
        display: none;
      }

      .x-scrollable-item {
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        background-color: #fff;
        min-width: 300px;
        width: 100%;
        padding: 55px 48px 30px 48px;
        border-radius: 8px;
        gap: 16px;
        .overlay-index {
          position: absolute;
          top: 10px;
          background-color: #0b55f5;
          border-radius: 4px;
          padding: 4px 8px;
          color: #fff;
          font-size: 12px;
          @include rtl-sass-prop(left, right, 10px);
        }
        img {
          width: 90px;
        }
        .description {
          font-weight: 400;
          color: #000;
        }
      }

      .y-scrollable-item {
        flex-direction: column;
        min-width: 148px;
        width: 148px;
        justify-content: flex-start;
        padding: 55px 16px 30px 16px;
        background-color: #f9f9f9;
        .description {
          width: 80%;
          font-weight: 400;
          color: #000;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          text-align: center;
          @supports (-webkit-line-clamp: 3) {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: initial;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
          }
        }
      }
    }

    // .overlay-scroll_left {
    //   position: sticky;
    //   display: flex;
    //   align-items: center;
    //   justify-content: center;
    //   top: 0;
    //   left: 0;
    //   // padding-right: 80px;
    //   // padding-left: 10px;
    //   background-image: linear-gradient(to left, transparent 0%, $white 100%);
    //   z-index: 100;
    // }

    // .overlay-scroll_right {
    //   position: sticky;
    //   display: flex;
    //   align-items: center;
    //   justify-content: center;
    //   right: 0;
    //   top: 0;
    //   padding-left: 80px;
    //   padding-right: 10px;
    //   background-image: linear-gradient(to right, transparent 0%, $white 100%);
    // }

    .overlay-scroll {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 20px;
      display: flex;
      align-items: center;
      cursor: pointer;
      animation: fadeIn 0.3s ease forwards;

      &.left {
        left: 0;
        background-image: linear-gradient(to left, transparent 0%, rgba($white, 0.8) 70%, $white 100%);
        padding-right: 20px;
        padding-left: 10px;
      }
      &.right {
        right: 0;
        background-image: linear-gradient(to right, transparent 0%, rgba($white, 0.8) 70%, $white 100%);
        padding-left: 20px;
        padding-right: 10px;
      }

      i {
        font-size: 20px;
        cursor: pointer;

        &:hover {
          color: #383428;
        }
      }
    }
  }
}

@media (max-width: 1024px) {
  #prizes {
    margin: 8px;

    .title {
      padding-top: 1rem;
    }
  }
}
</style>
